import React, { useEffect, useRef, useState } from 'react'
import { Button, Dropdown, message, Modal, Tooltip } from 'antd'
import { CheckCircleOutlined, ExclamationCircleOutlined, FileAddOutlined, FileSearchOutlined, PlayCircleOutlined, MoreOutlined, ReloadOutlined } from '@ant-design/icons'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { ReviewDocument } from './result-model'
import { onecDialogPresentation } from './onec-dialog'
import { OnecReadiness, TransferIssue, useOnecReadiness } from './onec-readiness'

const canCancel = [
  'preparing',
  'validating',
  'validation_failed',
  'awaiting_confirmation',
  'cancelling',
]
const uncertain = (transfer: any) =>
  ['executing', 'unknown', 'verification_pending'].includes(transfer?.status)
const expired = (transfer: any) => {
  const expiresAt = new Date(transfer?.remote_result?.validation_expires_at).getTime()
  return !Number.isFinite(expiresAt) || expiresAt <= Date.now() + 5000
}

function useOnecOperation({
  reviewId,
  document,
  dirty,
  posting = false,
  enabled = true,
  beforePrepare,
  onPrepareError,
}: {
  reviewId: string
  document: ReviewDocument
  dirty: boolean
  posting?: boolean
  enabled?: boolean
  beforePrepare?: () => Promise<boolean>
  onPrepareError?: (message: string) => void
}) {
  const [transfer, setTransfer] = useState<any>(null)
  const [available, setAvailable] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [busy, setBusy] = useState(false)
  const running = useRef(false)
  const mounted = useRef(true)
  const currentDocument = useRef({ version: document.version, dirty })
  currentDocument.current = { version: document.version, dirty }
  const [modal, modalHolder] = Modal.useModal()
  const [messageApi, messageHolder] = message.useMessage()
  const path = `/api/supply/reviews/${reviewId}/documents/${document.id}/onec-write${
    posting ? '/posting' : ''
  }`
  useEffect(() => {
    if (!enabled) return
    mounted.current = true
    let active = true
    const load = async () => {
      try {
        const result = await fetchAPI(path)
        if (active && !running.current) {
          setTransfer(result.data)
          setAvailable(true)
          setLoadError('')
        }
      } catch (error: any) {
        if (active && !running.current) {
          setAvailable(error.statusCode !== 403)
          setLoadError(error.message)
        }
      }
    }
    void load()
    const timer = setInterval(load, 15000)
    return () => {
      active = false
      mounted.current = false
      clearInterval(timer)
    }
  }, [path, enabled])

  const request = async (action?: string, operation?: any) => {
    const result = await fetchAPI(
      action ? `${path}/${action}` : path,
      action
        ? {
            method: 'POST',
            timeoutMs: 240000,
            body: JSON.stringify({
              ...(posting ? { id: operation?.id } : { version: document.version }),
              payload_hash: operation?.payload_hash,
            }),
          }
        : undefined,
    )
    if (result.error || result.errors)
      throw new Error(
        result.error?.message ||
          result.error ||
          result.errors?.[0]?.message ||
          'Не удалось выполнить действие с 1С',
      )
    const next = result.data?.status === 'cancelled' ? null : result.data
    if (mounted.current) {
      setTransfer(next)
      setLoadError('')
    }
    return next
  }
  const receiptDetails = (operation: any) =>
    modal.info({
      ...onecDialogPresentation({ operation, posting, result: true }),
      okText: 'Закрыть',
    })
  const send = async () => {
    if (running.current) return
    running.current = true
    setBusy(true)
    try {
      let operation = await request()
      if (['completed', 'already_posted', 'failed'].includes(operation?.status)) {
        receiptDetails(operation)
        return
      }
      if (uncertain(operation)) {
        operation = await request('reconcile', operation)
        if (['completed', 'already_posted', 'failed'].includes(operation.status))
          receiptDetails(operation)
        else
          messageApi.warning(
            posting
              ? 'Результат проведения пока не подтверждён. Повторите проверку; проведение заново не запускается.'
              : 'Результат пока неизвестен. Повторите проверку отправки; новая запись не запускается.',
          )
        return
      }
      if (
        operation &&
        canCancel.includes(operation.status) &&
        ((!posting && operation.document_version !== document.version) ||
          expired(operation) ||
          ['validating', 'validation_failed', 'cancelling'].includes(operation.status))
      ) {
        await request('cancel', operation)
        operation = null
      }
      if (beforePrepare && !(await beforePrepare())) return
      operation = await request('prepare', operation)
      if (!mounted.current) return
      if (posting && ['completed', 'already_posted'].includes(operation.status)) {
        receiptDetails(operation)
        return
      }
      if (operation.status !== 'awaiting_confirmation') {
        throw new Error(
          operation.error_message || 'Проверка в 1С ещё не завершена. Повторите отправку позже.',
        )
      }
      const prepared = operation
      await new Promise<void>(resolve => {
        const dialog = modal.confirm({
          ...onecDialogPresentation({ operation: prepared, posting }),
          okText: posting ? 'Провести в 1С' : 'Отправить в 1С',
          cancelText: posting ? 'Отменить подготовку' : 'Отмена',
          onCancel: async () => {
            if (posting) {
              try {
                await request('cancel', prepared)
              } catch (error: any) {
                messageApi.error(error.message)
              }
            }
            resolve()
          },
          onOk: async () => {
            dialog?.update?.({
              ...onecDialogPresentation({ operation: prepared, posting, pending: true }),
              okText: posting ? 'Проведение…' : 'Отправка…',
            })
            try {
              if (
                !mounted.current ||
                currentDocument.current.dirty ||
                (!posting && currentDocument.current.version !== prepared.document_version)
              )
                throw new Error(
                  'Накладная изменилась. Закройте окно и повторите отправку после подтверждения проверки.',
                )
              if (expired(prepared))
                throw new Error('Проверка истекла. Закройте окно и повторите подготовку.')
              let result = await request('confirm', prepared)
              if (['completed', 'already_posted'].includes(result.status)) {
                if (!posting) {
                  try { const latest = await request(); if (latest?.status === 'completed') result = latest } catch { /* The confirmed receipt remains available. */ }
                }
                messageApi.success(
                  posting
                    ? 'Проведение приходной накладной подтверждено в 1С'
                    : 'Приходная накладная создана в 1С без проведения',
                )
                receiptDetails(result)
              } else if (uncertain(result))
                messageApi.warning(
                  posting
                    ? 'Проведение пока не подтверждено. Используйте «Проверить проведение».'
                    : 'Результат пока неизвестен. Используйте «Проверить отправку».',
                )
              else
                messageApi.error(
                  result.error_message || 'Запись не выполнена. Проверьте результат отправки.',
                )
              resolve()
            } catch (error: any) {
              messageApi.error(error.message)
              try {
                const latest = await request()
                if (
                  uncertain(latest) ||
                  ['completed', 'already_posted', 'failed'].includes(latest?.status)
                ) {
                  resolve()
                  return
                }
              } catch {
                /* Preserve the last known operation. */
              }
              dialog?.update?.({
                ...onecDialogPresentation({ operation: prepared, posting, error: error.message }),
                okText: posting ? 'Провести в 1С' : 'Отправить в 1С',
              })
              throw error
            }
          },
        })
      })
    } catch (error: any) {
      if (mounted.current) {
        messageApi.error(error.message)
        onPrepareError?.(error.message)
        try {
          await request()
        } catch {
          /* Preserve the last known operation. */
        }
      }
    } finally {
      running.current = false
      if (mounted.current) setBusy(false)
    }
  }

  return { transfer, available: enabled && available, loadError, busy, send, modalHolder, messageHolder }
}

type Operation = ReturnType<typeof useOnecOperation>
const isCompleted = (operation: Operation) => ['completed', 'already_posted'].includes(operation.transfer?.status)
const hasResult = (operation: Operation) => isCompleted(operation) || operation.transfer?.status === 'failed'

function OperationAction({ operation, document, dirty, posting = false }: {
  operation: Operation; document: ReviewDocument; dirty: boolean; posting?: boolean
}) {
  if (!operation.available) return null
  const { transfer, loadError, busy, send } = operation
  if (hasResult(operation)) return <span className={`supply-check__onec-status ${isCompleted(operation) ? 'is-completed' : 'is-failed'}`}>
    {isCompleted(operation) ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
    {isCompleted(operation) ? posting ? 'Проведено в 1С' : 'Создано в 1С' : posting ? 'Ошибка проведения' : 'Ошибка отправки'}
  </span>
  return <Tooltip title={loadError || (uncertain(transfer) ? 'Проверить квитанцию текущей операции без повторной записи' : undefined)}>
    <Button
      type="primary"
      icon={uncertain(transfer) ? <ReloadOutlined /> : posting ? <PlayCircleOutlined /> : <FileAddOutlined />}
      loading={busy}
      disabled={document.status !== 'confirmed' || dirty || document.draft.matching_mode === 'unlinked'}
      onClick={send}
    >{uncertain(transfer) ? posting ? 'Проверить проведение' : 'Проверить отправку' : posting ? 'Провести в 1С' : 'Создать в 1С'}</Button>
  </Tooltip>
}

export default function OnecTransfer(props: {
  reviewId: string
  document: ReviewDocument
  dirty: boolean
  onResolve?: (issue: TransferIssue) => void
  onCreateOrder?: () => void
  readinessOpenSignal?: number
  createdSupplierOrder?: { number: string }
}) {
  const [canCheck, setCanCheck] = useState(false)
  const readiness = useOnecReadiness(props.reviewId, props.document, canCheck)
  useEffect(() => {
    if (!props.readinessOpenSignal) return
    readiness.setOpen(true)
    void readiness.refresh()
  }, [props.readinessOpenSignal])
  const sending = useOnecOperation({ ...props, beforePrepare: readiness.ensure, onPrepareError: readiness.report })
  useEffect(() => { setCanCheck(sending.available && !isCompleted(sending)) }, [sending.available, sending.transfer?.status])
  const posting = useOnecOperation({ ...props, posting: true, enabled: isCompleted(sending) && props.document.status === 'confirmed' && sending.transfer?.receipt?.posted !== true })
  const results = [
    { key: 'send', label: 'Результат отправки', operation: sending },
    { key: 'post', label: 'Результат проведения', operation: posting },
  ].filter(item => item.operation.available && hasResult(item.operation))
  return <>
    <React.Fragment key="sending">{sending.modalHolder}{sending.messageHolder}</React.Fragment>
    <React.Fragment key="posting">{posting.modalHolder}{posting.messageHolder}</React.Fragment>
    {sending.available && <div className="supply-check__onec-transfer">
      {!isCompleted(sending) && !uncertain(sending.transfer) && <OnecReadiness onCreateOrder={props.onCreateOrder} createdSupplierOrder={props.createdSupplierOrder} compact state={readiness} dirty={props.dirty} onResolve={props.onResolve} />}
      <OperationAction operation={sending} document={props.document} dirty={props.dirty} />
      {sending.transfer?.receipt?.posted !== true && <>{!isCompleted(sending) ? <Tooltip title="Сначала создайте приходную накладную"><Button type="primary" icon={<PlayCircleOutlined />} disabled>Провести в 1С</Button></Tooltip> : <OperationAction operation={posting} document={props.document} dirty={props.dirty} posting />}</>}
      {sending.transfer?.receipt?.number && <span className="supply-check__onec-receipt"><a href={`/warehouse/${encodeURIComponent(sending.transfer.receipt.number)}`} target="_blank" rel="noopener noreferrer">Приходная № {sending.transfer.receipt.number}</a><small>{sending.transfer.receipt.posted || isCompleted(posting) ? 'Проведена' : 'Без проведения'}</small></span>}
      {sending.transfer?.receipt_warning && <Tooltip title={sending.transfer.receipt_warning}><ExclamationCircleOutlined aria-label="Требуется проверить созданную приходную" /></Tooltip>}
      {!!results.length && <Dropdown trigger={['click']} placement="topRight" menu={{ items: results.map(item => ({ key: item.key, label: item.label, icon: <FileSearchOutlined />, disabled: item.operation.busy, onClick: () => { void item.operation.send() } })) }}>
        <Button icon={<MoreOutlined />} loading={results.some(item => item.operation.busy)}>Подробнее</Button>
      </Dropdown>}
    </div>}
  </>
}
